import { cloneDeep, uniq } from "lodash";

export const initialState = {
  activeSavedSearch: undefined,
  componentsLoaded: [],
  displayAsList: false,
  parameters: {
    expert_query: undefined,
    date_max: null,
    date_min: null,
    impacted_parties: [],
    in_order: false,
    regflows: undefined,
    size: 12,
    sources: [],
    strict_search: false,
    terms: [],
    topic_ids: [],
  },
  regAssist: {
    intro: {
      allowQuestion: false,
      selectedSearchType: undefined,
    },
    history: [],
  },
};

const searchReducer = (state, action) => {
  const { type, payload } = action;

  switch (type) {
    case "SEARCH_UPDATE_PARAMETERS": {
      return {
        activeSavedSearch: undefined,
        componentsLoaded: state.componentsLoaded,
        displayAsList: state.displayAsList,
        parameters: {
          ...state.parameters,
          ...payload.parameters,
        },
        regAssist: state.regAssist,
      };
    }

    case "SEARCH_ADD_TAG": {
      const newList = uniq([
        ...(state.parameters[payload.tag.type] || []),
        payload.tag.value,
      ]);
      return {
        ...state,
        activeSavedSearch: undefined,
        parameters: {
          ...state.parameters,
          [payload.tag.type]: newList,
        },
      };
    }

    case "SEARCH_CLEAR_PARAMETERS": {
      return {
        ...state,
        activeSavedSearch: undefined,
        parameters: initialState.parameters,
      };
    }

    case "SEARCH_SAVE": {
      return {
        ...state,
        activeSavedSearch: payload.data.id,
        parameters: {
          ...state.parameters,
          ...payload.data.parameters,
        },
      };
    }

    case "SEARCH_SET_ACTIVE_SAVED_SEARCH": {
      return {
        ...state,
        activeSavedSearch: payload.data,
      };
    }

    case "SEARCH_SET_DISPLAY_AS_LIST": {
      return {
        ...state,
        displayAsList: payload.displayAsList,
      };
    }

    case "SEARCH_REGASSIST_SEARCH_GENERATION": {
      // create a new chat session everytime search parameters change

      const history = [...state.regAssist.history];
      const [lastEntry] = history.slice(-1);

      // First we remove the last entry from history if it has no messages in it
      let newHistory = lastEntry?.messages.length
        ? history
        : history.slice(0, -1);
      const [newLastEntry] = newHistory.slice(-1);

      // Then we add a new entry, only if the search parameters does not match the last entry
      if (payload.key !== newLastEntry?.searchKey) {
        newHistory = [
          ...newHistory,
          {
            messages: [],
            params: payload.params,
            searchId: Date.now(),
            searchKey: payload.key,
            searchResultsCount: payload.totalResults,
          },
        ];
      }

      const hasSearchParameters =
        !!payload.params.date_max ||
        !!payload.params.date_min ||
        !!payload.params.impacted_parties?.length ||
        !!payload.params.regflows ||
        !!payload.params.sources?.length ||
        !!payload.params.strict_search ||
        !!payload.params.terms?.length ||
        !!payload.params.topic_ids?.length;

      return {
        ...state,
        regAssist: {
          intro: {
            allowQuestion:
              state.regAssist.intro.selectedSearchType ===
                "searchWithParameters" ||
              state.regAssist.intro.selectedSearchType === "allArticles" ||
              hasSearchParameters
                ? true
                : state.regAssist.intro.allowQuestion,
            selectedSearchType: state.regAssist.intro.selectedSearchType,
          },
          history: newHistory,
        },
      };
    }

    case "SEARCH_REGASSIST_SEARCH_TYPE": {
      return {
        ...state,
        regAssist: {
          ...state.regAssist,
          intro: {
            allowQuestion: payload.searchType === "allArticles",
            selectedSearchType: payload.searchType,
          },
        },
      };
    }

    case "SEARCH_ADD_REGASSIST_QUESTION": {
      // add question in the last chat session search
      const regAssistHistory = [...state.regAssist.history];
      const [lastSearch] = regAssistHistory.slice(-1);
      const newMessage = { prompt: payload.prompt, id: Date.now() };

      return {
        ...state,
        regAssist: {
          ...state.regAssist,
          error: undefined,
          history: [
            ...regAssistHistory.slice(0, -1),
            {
              ...lastSearch,
              messages: lastSearch?.messages
                ? [...lastSearch.messages, newMessage]
                : [newMessage],
            },
          ],
        },
      };
    }

    case "SEARCH_REGASSIST_HANDLE_RESPONSE": {
      const { data, messageId, searchId } = payload;
      const regAssistHistory = state.regAssist.history;

      const sessionIndex = regAssistHistory.findIndex(
        (hist) => hist.searchId === searchId
      );

      const messageIndex = regAssistHistory[sessionIndex].messages.findIndex(
        (msg) => msg.id === messageId
      );

      const messageHasResponse =
        !!regAssistHistory[sessionIndex].messages[messageIndex].response;

      if (messageHasResponse) {
        return state;
      } else {
        const newHistory = cloneDeep(state.regAssist.history);
        let message = newHistory[sessionIndex].messages[messageIndex];
        newHistory[sessionIndex].messages[messageIndex] = {
          ...message,
          ...data,
        };

        return {
          ...state,
          regAssist: {
            ...state.regAssist,
            error: undefined,
            history: newHistory,
          },
        };
      }
    }

    case "SEARCH_REGASSIST_HANDLE_ERROR": {
      // handle error from a first error (right after a new question from the user)
      const regAssistHistory = [...state.regAssist.history];
      const [lastSearch] = regAssistHistory.slice(-1);
      const newState = {
        ...state,
        regAssist: {
          ...state.regAssist,
          error: payload,
          history: [
            ...regAssistHistory.slice(0, -1),
            {
              ...lastSearch,
              messages: [...lastSearch.messages.slice(0, -1)],
            },
          ],
        },
      };
      return newState;
    }

    case "SEARCH_REGASSIST_RESET_HISTORY": {
      if (!state.regAssist.history.length) {
        return state;
      }

      const [lastSearch] = state.regAssist.history.slice(-1);
      const lastSearchCleanHistory = { ...lastSearch, messages: [] };

      return {
        ...state,
        regAssist: {
          ...state.regAssist,
          error: undefined,
          history: [lastSearchCleanHistory],
        },
      };
    }

    case "REGISTER_LOADED_COMPONENT": {
      return {
        ...state,
        componentsLoaded: [
          ...new Set([...state.componentsLoaded, payload.componentName]),
        ],
      };
    }

    default:
      return state;
  }
};

export default searchReducer;
